import { getApiUrl } from '../config/api';
import toastService from './toastService';
import AuthServiceV2 from './authServiceV2';
import { AUTH_ROLES, ROUTE_NAMESPACES } from '../utils/authConstants';

const getDefaultSuccessMessage = (method) => {
  const m = (method || 'GET').toUpperCase();
  if (m === 'POST') return 'Created successfully';
  if (m === 'PUT' || m === 'PATCH') return 'Updated successfully';
  if (m === 'DELETE') return 'Deleted successfully';
  return 'Success';
};

const getAuthHeaders = (role) => {
  const token = role ? AuthServiceV2.getToken(role) : localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const safeJson = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const getRoleFromEndpoint = (endpoint) => {
  if (endpoint.startsWith('/admin/')) return AUTH_ROLES.ADMIN;
  if (endpoint.startsWith('/reviewer/')) return AUTH_ROLES.REVIEWER;
  if (endpoint.startsWith('/user/')) return AUTH_ROLES.USER;
  // Default to current role or try to infer from path
  const currentPath = window.location.pathname;
  if (currentPath.startsWith(ROUTE_NAMESPACES.ADMIN)) return AUTH_ROLES.ADMIN;
  if (currentPath.startsWith(ROUTE_NAMESPACES.REVIEWER)) return AUTH_ROLES.REVIEWER;
  return AUTH_ROLES.USER;
};

const handle401Error = (role) => {
  // Show session expired message
  toastService.error('Session expired');
  
  // Clear the token for the current role
  if (role) {
    AuthServiceV2.clearTokens(role);
  }
  
  // Redirect to appropriate login based on current path
  const currentPath = window.location.pathname;
  let redirectRoute = '/login'; // default to user login
  
  if (currentPath.startsWith(ROUTE_NAMESPACES.ADMIN)) {
    redirectRoute = '/admin/login';
  } else if (currentPath.startsWith(ROUTE_NAMESPACES.REVIEWER)) {
    redirectRoute = '/reviewer/login';
  }
  
  // Only redirect if not already on a login page
  if (!currentPath.includes('/login')) {
    window.location.href = redirectRoute;
  }
};

/**
 * Standard API wrapper for backend responses:
 * { status: boolean, status_code: number, message: string|null, data: any }
 */
export const apiRequest = async ({
  endpoint,
  method = 'GET',
  body,
  headers,
  auth = true,
  showSuccess = false,
  successMessage,
  errorMessage,
  suppressErrorToast = false, // New option to prevent error toasts
  role = null // Allow explicit role specification
}) => {
  try {
    // Determine which role's token to use
    const targetRole = role || getRoleFromEndpoint(endpoint);
    
    const finalHeaders = {
      accept: 'application/json',
      ...(auth ? getAuthHeaders(targetRole) : { 'Content-Type': 'application/json' }),
      ...(headers || {})
    };

    const response = await fetch(getApiUrl(endpoint), {
      method,
      headers: finalHeaders,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {})
    });

    const result = await safeJson(response);

    // Handle 401 Unauthorized. Login/unauthenticated requests should show
    // the backend message, not clear tokens as an expired session.
    if (response.status === 401) {
      const msg = result?.message || 'Unauthorized';
      if (auth === false) {
        if (!suppressErrorToast) toastService.error(msg);
        return { success: false, message: msg, raw: result };
      }

      handle401Error(targetRole);
      return { success: false, message: msg, raw: result };
    }

    // Handle 429 Too Many Requests
    if (response.status === 429) {
      const msg = errorMessage || result?.message || 'Too many requests. Please try again later.';
      if (!suppressErrorToast) toastService.error(msg);
      return { success: false, message: msg, error: 'TOO_MANY_REQUESTS', raw: result };
    }

    const backendStatus = result?.status;
    const backendCode = result?.status_code;
    const backendMessage = result?.message;

    const isBackendSuccess = backendStatus === true && (backendCode === 200 || backendCode === 201);

    if (isBackendSuccess) {
      const msg = backendMessage ?? successMessage ?? getDefaultSuccessMessage(method);
      if (showSuccess) toastService.success(msg);
      return { success: true, data: result?.data, message: msg, raw: result };
    }

    const msg = backendMessage || errorMessage || `Request failed (${response.status})`;
    if (!suppressErrorToast) toastService.error(msg);
    return { success: false, message: msg, raw: result };
  } catch (err) {
    const msg = errorMessage || err?.message || 'Network error. Please check your connection.';
    if (!suppressErrorToast) toastService.error(msg);
    return { success: false, message: msg, error: err };
  }
};
